import { OrgUserSearchResult, UserId, WorkflowItem } from '@cibo/core'
import { Label, usePaginatedFields } from '@cibo/landmanager'
import { usePaginatedOrgUsers } from '@cibo/profile'
import { useWorkflows } from '@cibo/programs'
import { DataGridPro } from '@cibo/ui'
import { Avatar, CircularProgress, Stack, Typography } from '@mui/material'
import { DataGridProProps, GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { fieldCountsToColor } from 'utils'

const EligibleFieldCountCell = ({ userId, programId }: { userId: UserId; programId: string }) => {
  const { t } = useTranslation('impact/NewEnrollmentUserList')
  const enrollableFields = usePaginatedFields(0, 1, {
    owner: userId,
    availableForWorkflow: { programId },
  })

  if (enrollableFields.isLoading) {
    return (
      <Typography color="text.disabled">
        <CircularProgress size="small" color="inherit" />
      </Typography>
    )
  }

  return (
    <>
      {!!enrollableFields.data?.numAvailable
        ? t('fields', { count: enrollableFields.data?.numAvailable })
        : '--'}
    </>
  )
}

export interface NewEnrollmentUserItem extends OrgUserSearchResult {
  existingEnrollment?: WorkflowItem
}

export const NewEnrollmentUserList = ({
  onRowClick,
  programId,
}: Pick<DataGridProProps<NewEnrollmentUserItem>, 'onRowClick'> & { programId: string }) => {
  const { t } = useTranslation('impact/NewEnrollmentUserList')
  const paginatedUsers = usePaginatedOrgUsers({ persona: 'grower' })
  const enrollments = useWorkflows(0, 1000, {
    benchmark: ['enrollment'],
    programId,
  })

  const columns = (programId: string, onRowClick: any) =>
    [
      {
        field: 'name',
        flex: 3,
        valueGetter: ({ row: { familyName, givenName, email } }) =>
          !!familyName ? `${familyName}${givenName}` : email,
        renderCell: ({ row: { familyName, givenName, email } }) => (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar />
              <Stack>
                <Typography variant="body2">
                  {!!familyName || !!givenName ? `${givenName} ${familyName}` : email}
                </Typography>
                {(!!familyName || !!givenName) && (
                  <Typography variant="caption">{email}</Typography>
                )}
              </Stack>
            </Stack>
          </>
        ),
      },
      {
        field: 'existingEnrollment',
        flex: 1.25,
        renderCell: (rowParams: GridRenderCellParams<WorkflowItem>) =>
          !!rowParams.value ? (
            <Label
              sx={{ pointerEvents: 'none' }}
              label={t('existingEnrollment', {
                count: rowParams.value.fieldCounts.total,
              })}
              color={fieldCountsToColor(rowParams.value.fieldCounts)}
              size="small"
            />
          ) : null,
      },
      {
        field: 'fields',
        flex: 0.75,
        valueGetter: () => 0,
        align: 'right',
        renderCell: ({ row }) => (
          <EligibleFieldCountCell userId={row.userId} programId={programId} />
        ),
      },
    ] as GridColumns<NewEnrollmentUserItem>

  const rows = useMemo(
    () =>
      paginatedUsers.data?.items.map(user => ({
        ...user,
        existingEnrollment: enrollments.data?.items.find(
          ({ ownedBy: { userId } }) => userId === user.userId
        ),
      })),
    [paginatedUsers.data?.items, enrollments.data?.items]
  )

  return (
    <DataGridPro<NewEnrollmentUserItem>
      //@ts-ignore the column model expects a POJO, but we provide a class ¯\_(ツ)_/¯
      columns={columns(programId, onRowClick)}
      rows={rows || []}
      loading={paginatedUsers.isLoading || enrollments.isLoading}
      onRowClick={rowParams => {
        if (!rowParams.row.existingEnrollment) {
          // @ts-ignore this is saying it needs three args but it doesn't!
          onRowClick(rowParams)
        }
      }}
      getRowClassName={rowParams => (!!rowParams.row.existingEnrollment ? 'disabled' : '')}
      getRowId={p => p.userId}
      pagination={true}
      rowsPerPageOptions={[10, 25]}
      headerHeight={0}
      disableSelectionOnClick
      hideBorders
      hideCellFocus
      autoHeight
    />
  )
}
